import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Calendar, User } from 'lucide-react';
import { Button } from '@shared/components/ui/button';
import { useResendInvitation } from '../../hooks/useResendInvitation';
import { useCancelInvitation } from '../../hooks/useCancelInvitation';
import { useRoles } from '../../hooks/useRoles';
import { PendingInvitation } from '../../models/User';

interface InvitationItemProps {
  invitation: PendingInvitation;
}

/**
 * Componente que muestra un item individual de invitación pendiente
 */
export const InvitationItem: React.FC<InvitationItemProps> = ({ invitation }) => {
  const { t } = useTranslation('usersModule');
  const { roles } = useRoles();
  const resendMutation = useResendInvitation();
  const cancelMutation = useCancelInvitation();

  const [showConfirmCancel, setShowConfirmCancel] = useState(false);

  // Obtener nombre del rol
  const roleName = roles.find((r) => r.id === invitation.role_id)?.name || `Rol #${invitation.role_id}`;

  // Formatear fechas
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const expiresAt = formatDate(invitation.expires_at);
  const createdAt = formatDate(invitation.created_at);

  // Manejar reenvío
  const handleResend = async () => {
    try {
      await resendMutation.mutateAsync(invitation.id);
    } catch (error) {
      // El error ya se maneja en el hook
      console.error('Error al reenviar invitación:', error);
    }
  };

  // Manejar cancelación
  const handleCancel = async () => {
    if (!showConfirmCancel) {
      setShowConfirmCancel(true);
      return;
    }

    try {
      await cancelMutation.mutateAsync(invitation.id);
      setShowConfirmCancel(false);
    } catch (error) {
      // El error ya se maneja en el hook
      console.error('Error al cancelar invitación:', error);
      setShowConfirmCancel(false);
    }
  };

  const isResending = resendMutation.isPending;
  const isCancelling = cancelMutation.isPending;
  const isProcessing = isResending || isCancelling;

  return (
    <div className="p-4 border border-card-border rounded-lg bg-card hover:bg-accent/20 transition-colors">
      <div className="flex items-start justify-between gap-4">
        {/* Información de la invitación */}
        <div className="flex-1 space-y-2">
          {/* Email */}
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-complementary-text" />
            <span className="text-sm font-medium text-foreground">{invitation.email}</span>
          </div>

          {/* Rol */}
          <div className="flex items-center gap-2">
            <User className="h-4 w-4 text-complementary-text" />
            <span className="text-sm text-complementary-text">
              {t('invitations.item.role', 'Rol')}: <span className="font-medium">{roleName}</span>
            </span>
          </div>

          {/* Fechas */}
          <div className="flex items-center gap-2 text-xs text-complementary-text">
            <Calendar className="h-3 w-3" />
            <span>
              {t('invitations.item.createdAt', 'Creada')}: {createdAt}
            </span>
            <span className="mx-2">•</span>
            <span>
              {t('invitations.item.expiresAt', 'Expira')}: {expiresAt}
            </span>
          </div>
        </div>

        {/* Acciones */}
        <div className="flex items-center gap-2 shrink-0">
          {showConfirmCancel ? (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowConfirmCancel(false)}
                disabled={isProcessing}
                data-testid={`cancel-confirm-cancel-${invitation.id}`}
              >
                {t('invitations.actions.cancelConfirm', 'No')}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={isProcessing}
                data-testid={`confirm-cancel-${invitation.id}`}
              >
                {isCancelling ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    {t('invitations.actions.cancelling', 'Cancelando...')}
                  </>
                ) : (
                  t('invitations.actions.confirmCancel', 'Confirmar')
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handleResend}
                disabled={isProcessing}
                data-testid={`resend-invitation-${invitation.id}`}
              >
                {isResending ? (
                  <>
                    <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                    {t('invitations.actions.resending', 'Reenviando...')}
                  </>
                ) : (
                  t('invitations.actions.resend', 'Reenviar')
                )}
              </Button>
              <Button
                variant="destructive"
                size="sm"
                onClick={handleCancel}
                disabled={isProcessing}
                data-testid={`cancel-invitation-${invitation.id}`}
              >
                {t('invitations.actions.cancel', 'Cancelar')}
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
